// useFixtureRotateEvents.js
//
// Armatür (LightFixture) rotate gizmo interaksiyon handler'ı. Pattern
// useSocketGizmoEvents'tan uyarlandı; iki temel fark:
//
//   1. Socket'te `offsetGroup.rotation.z = wireAngle + userRotate` döner.
//      Fixture'da `group.rotation.z = userRotate` döner (createLightFixture-
//      Symbols.js:314). Dolayısıyla rotation'ı GROUP'a yazıyoruz.
//
//   2. Fixture textGroup `rotation.z = -rotateValue` ile counter-rotate
//      ediliyor (factory line 277) ki etiket okunabilir kalsın. Group'u
//      döndürdüğümüzde textGroup'u da senkronize ediyoruz.
//
// Mesh-FP koordinat fix'i (Bug 4'le aynı kök): center için .position değil
// .getWorldPosition kullan — fixture fpMesh-child olabilir.

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSelector } from 'react-redux';
import { throttleRAF } from '../../../utils/throttle';

const findObjectInScene = (scene, predicate) => {
  let result = null;
  scene.traverse(obj => {
    if (!result && predicate(obj)) result = obj;
  });
  return result;
};

export default function useFixtureRotateEvents({ scene, camera, renderer }) {
  const rotatingFixtureRef = useRef(null);
  const startAngleRef = useRef(0);
  const startRotationZRef = useRef(0);

  const orthoMode = useSelector((state) => state.mods.orthoMode);

  useEffect(() => {
    if (!scene || !camera || !renderer) return;

    const dom = renderer.domElement;
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const getWorldPoint = (event) => {
      const rect = dom.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
      const point = new THREE.Vector3();
      raycaster.ray.intersectPlane(plane, point);
      return point;
    };

    const handleMouseDown = (event) => {
      const point = getWorldPoint(event);
      const intersects = raycaster.intersectObjects(scene.children, true);
      const rotateHit = intersects.find(i =>
        i.object?.userData?.type === 'rotateGizmo' &&
        i.object?.userData?.parentFixtureId
      );
      if (!rotateHit) return;

      const parentId = rotateHit.object.userData.parentFixtureId;
      const fixtureGroup = findObjectInScene(scene, obj =>
        obj.userData?.id === parentId && obj.userData?.type === 'LightFixture'
      );
      if (!fixtureGroup) return;

      // fpMesh-child olabilir; world position gerekli (Bug 4 kök neden).
      const center = fixtureGroup.getWorldPosition(new THREE.Vector3());
      const startVector = new THREE.Vector2(point.x - center.x, point.y - center.y);
      startAngleRef.current = Math.atan2(startVector.y, startVector.x);
      startRotationZRef.current = fixtureGroup.rotation.z;
      rotatingFixtureRef.current = fixtureGroup;
    };

    const handleMouseMove = (event) => {
      if (!rotatingFixtureRef.current) return;
      const point = getWorldPoint(event);
      const center = rotatingFixtureRef.current.getWorldPosition(new THREE.Vector3());
      const currentVector = new THREE.Vector2(point.x - center.x, point.y - center.y);
      let currentAngle = Math.atan2(currentVector.y, currentVector.x);

      if (orthoMode) {
        const rawDelta = currentAngle - startAngleRef.current;
        const deltaDeg = THREE.MathUtils.radToDeg(rawDelta);
        const snappedDeg = Math.round(deltaDeg / 45) * 45;
        currentAngle = startAngleRef.current + THREE.MathUtils.degToRad(snappedDeg);
      }

      const delta = currentAngle - startAngleRef.current;
      const newRotZ = startRotationZRef.current + delta;
      rotatingFixtureRef.current.rotation.z = newRotZ;

      // textGroup counter-rotate (factory pattern: textGroup.rotation.z = -rotateValue)
      const textGroup = rotatingFixtureRef.current.children.find(c => c.name === 'textGroup');
      if (textGroup) textGroup.rotation.z = -newRotZ;

      // userData.rotate (derece) save/load için (factory rotateValue parametresi
      // bunu okur → reload sonrası mesh aynı açıda doğar).
      rotatingFixtureRef.current.userData.rotate = (newRotZ * 180) / Math.PI;
    };

    const handleMouseUp = () => {
      rotatingFixtureRef.current = null;
    };

    const throttledMouseMove = throttleRAF(handleMouseMove);

    dom.addEventListener('mousedown', handleMouseDown);
    dom.addEventListener('mousemove', throttledMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      throttledMouseMove.cancel();
      dom.removeEventListener('mousedown', handleMouseDown);
      dom.removeEventListener('mousemove', throttledMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [scene, camera, renderer, orthoMode]);
}
